import { type RefObject } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import { type CSSProperties } from '@mui/material/styles';
import {
   type GridAlignment,
   type GridApiPremium,
   type GridColDef,
   gridDataRowIdsSelector,
   type GridExcelExportOptions,
   gridFilterModelSelector,
   type GridInitialState,
   gridPaginationModelSelector,
   gridPaginationRowCountSelector,
   type GridSortModel,
   gridSortModelSelector,
   type GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { code } from 'currency-codes';
import { attributesToProps, type DOMNode, domToReact, type Element } from 'html-react-parser';
import { flatten, isEmpty, map, reduce, sortBy } from 'lodash-es';
import { z } from 'zod';
import { type NotificationContext } from 'components/v2/NotificationsPopover/types';
import type CRUDService from 'state/CRUDService';
import { KPIFieldType, type KPIFieldTypeEnum, SearchResultKind, type SearchResultKindEnum } from 'utils/enum';

export const MAX_PAGE_SIZE = 100;

export const CONVERSION_FACTOR_EXCLUDED_TYPES: KPIFieldTypeEnum[] = [
   KPIFieldType.FORMULA,
   KPIFieldType.PRICE,
   KPIFieldType.PERCENTAGE,
   KPIFieldType.NUMBER,
] as const;
export const CONVERSION_FIELDTYPE_EXCLUDED_TYPES: KPIFieldTypeEnum[] = [
   ...CONVERSION_FACTOR_EXCLUDED_TYPES,
   KPIFieldType.EMISSIONS,
   KPIFieldType.EMISSION_SELECT,
] as const;

export const emailPattern = z.regexes.email;

export const checkObjectKeys = (object: Record<string, any>, value: string): boolean => Object.keys(object).some((key) => key.startsWith(value));

export function truncateNumber(numToTruncate: number | string, decimalPlaces = 100): number {
   // eslint-disable-next-line security/detect-non-literal-regexp
   const re = new RegExp(`(\\d+\\.\\d{${decimalPlaces}})(\\d)`);
   const m = re.exec(numToTruncate.toString());

   if (m) {
      return parseFloat(m[1]);
   }

   if (typeof numToTruncate === 'number') {
      return numToTruncate.valueOf();
   }

   return parseFloat(numToTruncate);
}

export type Option = Record<string, any>;

export function createMenuItems(objects: Option[] = [], key = 'id', value = 'value', label = 'label') {
   return objects.map((option) => (
      <MenuItem key={option[key]} value={option[value]}>
         {option[label]}
      </MenuItem>
   ));
}

export function sanitizeSortModel(sortModel: any): GridSortModel {
   if (!sortModel || (typeof sortModel === 'object' && isEmpty(sortModel))) {
      return [];
   }

   return Array.isArray(sortModel) ? sortModel : [sortModel];
}

function isElement(domNode: DOMNode): domNode is Element {
   const isTag = domNode.type?.valueOf() === 'tag';
   const hasAttributes = (domNode as Element).attribs != undefined;

   return isTag && hasAttributes;
}

export function openHyperlinkInNewWindowReplaceFunction(domNode: DOMNode) {
   if (isElement(domNode) && domNode.name === 'a') {
      const props = attributesToProps(domNode.attribs);

      return (
         <a {...props} rel="noopener noreferrer" target="_blank">
            {domToReact(domNode.children as DOMNode[])}
         </a>
      );
   }
}

export function generateObjectURL({
   modelType,
   id,
   organisationId,
   from,
   to,
   context,
}: {
   modelType: SearchResultKindEnum;
   id: string | number;
   organisationId: number;
   from: string;
   to: string;
   context?: NotificationContext;
}) {
   const params = new URLSearchParams();

   params.set('organisationId', organisationId.toString());
   params.set('from', from);
   params.set('to', to);

   let basePath;

   switch (modelType) {
      case SearchResultKind.REPORT:
         basePath = `reports/${id}`;
         break;
      case SearchResultKind.BUSINESS_ACTIVITY:
         basePath = `taxonomy/businessactivities/${id}`;
         break;
      case SearchResultKind.EMISSION_FACTOR:
         basePath = `emissions/factors/${id}`;
         break;
      case SearchResultKind.TAXONOMYRESPONSEATTACHMENT:
      case SearchResultKind.TAXONOMYCRITERIARESPONSE:
         basePath = `taxonomy/businessactivities/${context?.businessActivityId}#${id}`;
         break;
      case SearchResultKind.FOOTPRINT:
         basePath = `emissions/ghg/footprints/${id}/false`;
         break;
      case SearchResultKind.FOOTPRINT_SUB:
         basePath = `emissions/ghg/footprints/${context?.footprintId}/false/${id}`;
         break;
      case SearchResultKind.FOOTPRINT_CALCULATION:
         basePath = `emissions/ghg/footprints/${context?.footprintId}/false/${context?.footprintSubId}/${id}`;
         break;
      case SearchResultKind.FOOTPRINT_CALCULATION_FIELD:
         basePath = `emissions/ghg/footprints/${context?.footprintId}/false/${context?.footprintSubId}/${context?.fpCalculationId}/${id}`;
         break;
      case SearchResultKind.KPI_AREA:
         basePath = `kpi/${id}`;
         break;
      case SearchResultKind.KPI:
         basePath = `kpi/${context?.kpiAreaId}/${id}`;
         break;
      case SearchResultKind.KPI_SUB:
         basePath = `kpi/${context?.kpiAreaId}/${context?.kpiId}/${id}`;
         break;
      case SearchResultKind.KPI_CONTENT:
         basePath = `kpi/${context?.kpiAreaId}/${context?.kpiId}/${context?.kpiSubId}/${id}`;
         break;
      case SearchResultKind.KPI_FIELD:
         basePath = `kpi/${context?.kpiAreaId}/${context?.kpiId}/${context?.kpiSubId}/${context?.kpiContentId}/${id}`;
         break;
      case SearchResultKind.USER:
         basePath = `users/${context?.userName}`;
         break;
      case SearchResultKind.TASK:
         basePath = `tasks/${id}`;
         break;
      case SearchResultKind.GROUP:
         basePath = 'settings/rights';
         break;
      case SearchResultKind.ORGANISATION:
         basePath = 'settings/organisations';
         break;
      case SearchResultKind.CURRENCY:
      case SearchResultKind.CURRENCYRATE:
         basePath = 'settings/currencies';
         break;
      case SearchResultKind.REPORTING_STANDARD:
         basePath = 'settings/reporting-standards';
         break;
      default:
         break;
   }

   const url = basePath ? `/${basePath}?${params}` : undefined;
   return url?.includes('undefined') ? undefined : url;
}

export function isNumericToken(string: string) {
   // eslint-disable-next-line security/detect-unsafe-regex
   const numericTokenRegex = /^\d+(?:[.,]\d+)?$/;
   return numericTokenRegex.test(string.trim());
}

export function isValidCurrencyCode(currencyCode: string) {
   return !!currencyCode && !!code(currencyCode);
}

export function saveSnapshot(apiRef: RefObject<GridApiPremium | null>, dataGridIdentifier: string) {
   if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();

      localStorage.setItem(`dataGridState_${dataGridIdentifier}`, JSON.stringify(currentState));
   }
   return false;
}

export function restoreState(dataGridIdentifier: string) {
   const stateFromLocalStorage = localStorage?.getItem(`dataGridState_${dataGridIdentifier}`);

   let parsedState: GridInitialState | undefined;
   if (stateFromLocalStorage) {
      try {
         parsedState = {
            ...JSON.parse(stateFromLocalStorage),
            pagination: {
               paginationModel: {
                  page: 0,
                  pageSize: 25,
               },
               rowCount: undefined,
            },
         };
      } catch {
         // Ignore corrupted localStorage data and use defaults
         localStorage.removeItem(`dataGridState_${dataGridIdentifier}`);
      }
   }

   return parsedState;
}

export const fullPageHeight = `calc(100vh - 70px - ${window.location.hostname.includes('.demo.envoria.app') ? 30 : 0}px)`;

export function cartesian(...args: number[][]) {
   return reduce(
      args,
      function (a: number[][], b: number[]) {
         return flatten(
            map(a, function (x: number[]) {
               return map(b, function (y: number) {
                  return x.concat([y]);
               });
            })
         );
      },
      [[]]
   );
}

export const priorityToNumber: Record<'LOW' | 'MEDIUM' | 'HIGH', number> = {
   HIGH: 3,
   MEDIUM: 2,
   LOW: 1,
};

export function computeColumnsInOrder(columns: GridColDef[], orderedFields: string[] | undefined): GridColDef[] {
   if (!orderedFields) {
      return columns;
   }

   return sortBy(columns, (col) => orderedFields.indexOf(col.field));
}

export const alignmentTypeToJustification: Record<GridAlignment, CSSProperties['justifyContent']> = {
   left: 'flex-start',
   center: 'center',
   right: 'flex-end',
};
export async function handleServerSideExcelDownload({
   apiRef,
   api,
   options = {},
   extraQueryParams,
}: {
   apiRef: RefObject<GridApiPremium | null>;
   api: CRUDService<any>;
   options?: GridExcelExportOptions;
   extraQueryParams?: Record<string, string>;
}) {
   if (!apiRef.current) return;

   apiRef.current.setLoading(true);

   // Save current row IDs to restore later
   const currentRowIds = gridDataRowIdsSelector(apiRef);
   const totalRowCount = gridPaginationRowCountSelector(apiRef);
   const paginationModel = gridPaginationModelSelector(apiRef);
   const sortModel = gridSortModelSelector(apiRef);
   const filterModel = gridFilterModelSelector(apiRef);

   const pageCount = Math.ceil(totalRowCount / MAX_PAGE_SIZE);

   const pageParams = api.buildDataSourcePageQueryParams({ paginationModel });
   const queryParams = await api.buildDataSourceQueryParams({ sortModel, filterModel });
   Object.entries(extraQueryParams ?? {}).forEach(([key, value]) => queryParams.set(key, value));

   pageParams.set('limit', MAX_PAGE_SIZE.toString());

   try {
      const pages = await Promise.all(
         Array.from({ length: pageCount }, (_, index) => api.getPage(index + 1, pageParams, queryParams).then(({ data }) => data))
      );

      const allRows = pages.flat() as GridValidRowModel[];

      apiRef.current.updateRows(allRows);

      const allRowIds = allRows.map((row) => row.id);

      await apiRef.current.exportDataAsExcel({
         ...options,
         getRowsToExport: () => allRowIds,
      });

      const idsToDelete = allRows.map((row) => row.id).filter((id) => !currentRowIds.includes(id));

      // Restore to original rows
      apiRef.current.updateRows(idsToDelete.map((id) => ({ id, _action: 'delete' })));
   } finally {
      apiRef.current.setLoading(false);
   }
}

function sanitizeEmissionUnit(unit?: string | null) {
   if (!unit) {
      return unit;
   }
   return unit.replace(/CO[2₂]e?$/g, 'CO₂').replace(/\BCO₂$/g, 'CO₂');
}

export function isCO2Unit(unit: string | undefined) {
   const normalized = sanitizeEmissionUnit(unit ?? '');
   return normalized ? /CO₂$/i.test(normalized) : false;
}

export const PERCENTAGE_END_ADORNMENT = <InputAdornment position="end">%</InputAdornment>;
